import { Body2, borderRadius, fontSizes, styled } from "@iventis/styles";
import { Theme } from "@emotion/react";
import React, { FunctionComponent } from "react";

import { useTheme } from "@mui/material";

interface GhostNodeComponentProps {
    count: number;
    name: string;
    getIconFunc: () => React.ReactNode;
    // The maximum length of the ghost (in pixels). Defaults to 250
    maxLength?: number;
}

export const GhostNodeComponent: FunctionComponent<GhostNodeComponentProps> = ({ count, getIconFunc, name, maxLength = 250 }) => {
    const theme = useTheme<Theme>();

    return (
        <GhostContainer theme={theme} nameLength={name.length} maxLength={maxLength}>
            <GhostIcon getIconFunc={getIconFunc} count={count} showCount />
            <Body2>{name}</Body2>
        </GhostContainer>
    );
};

const GhostContainer = styled.div<{ maxLength: number; nameLength: number }>`
    position: absolute;
    background-color: ${({ theme }: { theme: Theme }) => theme.secondaryColors.blank};

    display: flex;
    flex-direction: row;
    gap: 5px;
    align-items: center;

    padding: 5px 7px 5px 7px;
    border-radius: ${borderRadius.standard};
    border-color: ${({ theme }: { theme: Theme }) => theme.shades.lightBorder};
    border-style: solid;
    border-width: 0 1px 1px 0;

    max-width: ${({ maxLength }) => maxLength}px;

    opacity: 90%;

    // Fade out only if the length of our name has caused an overflow
    mask-image: linear-gradient(90deg, black ${({ nameLength, maxLength }) => (nameLength > Math.floor(maxLength / 8) ? "60%" : "100%")}, transparent);

    p {
        text-overflow: ellipsis;
        white-space: nowrap;
        overflow: hidden;
    }
`;

interface GhostIconProps {
    getIconFunc: () => React.ReactNode;
    showCount?: boolean;
    count?: number;
}

const GhostIcon: FunctionComponent<GhostIconProps> = ({ getIconFunc, count, showCount }) => {
    const baseIcon = getIconFunc();

    return (
        <StyledMultiGhostIconContainer>
            {baseIcon}
            {showCount && <StyledGhostChildCount>{count}</StyledGhostChildCount>}
        </StyledMultiGhostIconContainer>
    );
};

const StyledMultiGhostIconContainer = styled.div`
    position: relative;
    height: 15px;
    width: 15px;
    margin: 0 10px 10px 0; // Make margin wrap the relatively positioned ghost child count
`;

const StyledGhostChildCount = styled.p`
    position: relative;
    bottom: 10px;
    right: -10px;
    color: ${({ theme }: { theme: Theme }) => theme.typographyColors.blank};
    background-color: ${({ theme }: { theme: Theme }) => theme.tertiaryColors.primary};
    font-weight: 400;
    font-size: ${fontSizes.xSmall};
    text-align: center;
    border-radius: ${borderRadius.circle};
    width: 15px;
    height: 15px;
`;
